import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  height: fit-content;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    if (props.$variant === 'success') return '#51cf66';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BitDisplay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 800px;
`;

const NumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const NumberLabel = styled.div`
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  min-width: 80px;
  text-align: center;
`;

const BitContainer = styled.div`
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Bit = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => {
    if (props.$isHighlighted) return '#ffd43b';
    if (props.$isChanged) return '#51cf66';
    if (props.$isResult) return '#4ecdc4';
    return 'rgba(255, 255, 255, 0.9)';
  }};
  color: #333;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: ${props => props.$isHighlighted ? '2px solid #ff6b6b' : 'none'};
  position: relative;
`;

const BitIndex = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 0.7rem;
  font-weight: 500;
`;

const DecimalValue = styled.div`
  color: white;
  font-weight: 500;
  font-size: 1rem;
  min-width: 60px;
  text-align: center;
`;

const HexValue = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  font-size: 0.9rem;
  min-width: 60px;
  text-align: center;
`;

const OperationDisplay = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const OperationTitle = styled.h3`
  color: white;
  margin: 0 0 15px 0;
  text-align: center;
  font-size: 1.3rem;
`;

const OperationSteps = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const StepItem = styled.div`
  margin-bottom: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  border-left: 3px solid #4ecdc4;
`;

const InfoPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1.2rem;
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const StatusBar = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StatusText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const AlgorithmSelector = styled.select`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

function BitManipulationPage() {
  const [num1, setNum1] = useState(42);
  const [num2, setNum2] = useState(23);
  const [selectedOperation, setSelectedOperation] = useState('and');
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [highlightedBits, setHighlightedBits] = useState([]);
  const [operationResult, setOperationResult] = useState(null);
  const [operationSteps, setOperationSteps] = useState([]);

  const operations = {
    and: {
      name: 'Bitwise AND (&)',
      description: 'Performs AND operation on each bit position',
      symbol: '&',
      complexity: 'Time: O(1), Space: O(1)'
    },
    or: {
      name: 'Bitwise OR (|)',
      description: 'Performs OR operation on each bit position',
      symbol: '|',
      complexity: 'Time: O(1), Space: O(1)'
    },
    xor: {
      name: 'Bitwise XOR (^)',
      description: 'Performs XOR operation on each bit position',
      symbol: '^',
      complexity: 'Time: O(1), Space: O(1)'
    },
    not: {
      name: 'Bitwise NOT (~)',
      description: 'Inverts all bits (unary operation)',
      symbol: '~',
      complexity: 'Time: O(1), Space: O(1)'
    },
    leftShift: {
      name: 'Left Shift (<<)',
      description: 'Shifts bits to the left by specified amount',
      symbol: '<<',
      complexity: 'Time: O(1), Space: O(1)'
    },
    rightShift: {
      name: 'Right Shift (>>)',
      description: 'Shifts bits to the right by specified amount',
      symbol: '>>',
      complexity: 'Time: O(1), Space: O(1)'
    },
    countSetBits: {
      name: 'Count Set Bits',
      description: 'Counts the number of 1s in binary representation',
      symbol: 'Count',
      complexity: 'Time: O(log n), Space: O(1)'
    },
    isPowerOfTwo: {
      name: 'Check Power of Two',
      description: 'Determines if a number is a power of 2',
      symbol: 'Power?',
      complexity: 'Time: O(1), Space: O(1)'
    }
  };

  const decimalToBinary = (num) => {
    if (num < 0) {
      // Handle negative numbers (32-bit representation)
      num = (num >>> 0).toString(2);
      return num.padStart(32, '0');
    }
    return num.toString(2).padStart(32, '0');
  };

  const decimalToHex = (num) => {
    if (num < 0) {
      return (num >>> 0).toString(16).toUpperCase();
    }
    return num.toString(16).toUpperCase();
  };

  const performOperation = async () => {
    setIsVisualizing(true);
    setHighlightedBits([]);
    setOperationResult(null);
    setOperationSteps([]);
    
    const binary1 = decimalToBinary(num1);
    let result;
    let steps = [];

    switch (selectedOperation) {
      case 'and':
        result = num1 & num2;
        steps = [
          `Perform AND operation on each bit position`,
          `1 & 1 = 1, 1 & 0 = 0, 0 & 1 = 0, 0 & 0 = 0`,
          `Result: ${num1} & ${num2} = ${result}`
        ];
        break;
        
      case 'or':
        result = num1 | num2;
        steps = [
          `Perform OR operation on each bit position`,
          `1 | 1 = 1, 1 | 0 = 1, 0 | 1 = 1, 0 | 0 = 0`,
          `Result: ${num1} | ${num2} = ${result}`
        ];
        break;
        
      case 'xor':
        result = num1 ^ num2;
        steps = [
          `Perform XOR operation on each bit position`,
          `1 ^ 1 = 0, 1 ^ 0 = 1, 0 ^ 1 = 1, 0 ^ 0 = 0`,
          `Result: ${num1} ^ ${num2} = ${result}`
        ];
        break;
        
      case 'not':
        result = ~num1;
        steps = [
          `Perform NOT operation on each bit`,
          `Invert all bits: 1 becomes 0, 0 becomes 1`,
          `Result: ~${num1} = ${result}`
        ];
        break;
        
      case 'leftShift':
        result = num1 << 1;
        steps = [
          `Shift all bits to the left by 1 position`,
          `Leftmost bit is lost, rightmost becomes 0`,
          `Result: ${num1} << 1 = ${result}`
        ];
        break;
        
      case 'rightShift':
        result = num1 >> 1;
        steps = [
          `Shift all bits to the right by 1 position`,
          `Rightmost bit is lost, leftmost becomes 0`,
          `Result: ${num1} >> 1 = ${result}`
        ];
        break;
        
      case 'countSetBits':
        result = (num1 >>> 0).toString(2).split('1').length - 1;
        steps = [
          `Convert ${num1} to binary: ${binary1}`,
          `Count the number of 1s in the binary representation`,
          `Result: ${result} set bits`
        ];
        break;
        
      case 'isPowerOfTwo':
        result = num1 > 0 && (num1 & (num1 - 1)) === 0;
        steps = [
          `Check if ${num1} is a power of 2`,
          `A number is a power of 2 if it has exactly one set bit`,
          `Result: ${num1} is ${result ? '' : 'not '}a power of 2`
        ];
        break;
        
      default:
        result = 0;
        steps = ['Invalid operation'];
    }

    setOperationResult(result);
    setOperationSteps(steps);

    // Animate the operation
    await new Promise(resolve => setTimeout(resolve, 500));
    setCurrentStep('Operation completed!');
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const renderBitRow = (label, number, isResult = false) => {
    const binary = decimalToBinary(number);
    const hex = decimalToHex(number);
    
    return (
      <NumberRow>
        <NumberLabel>{label}</NumberLabel>
        <BitContainer>
          {binary.split('').map((bit, index) => (
            <Bit
              key={index}
              $isHighlighted={highlightedBits.includes(index)}
              $isChanged={isResult && operationResult !== null}
              $isResult={isResult}
            >
              {bit}
              <BitIndex>{31 - index}</BitIndex>
            </Bit>
          ))}
        </BitContainer>
        <DecimalValue>{number}</DecimalValue>
        <HexValue>0x{hex}</HexValue>
      </NumberRow>
    );
  };

  const generateRandomNumbers = () => {
    const random1 = Math.floor(Math.random() * 100);
    const random2 = Math.floor(Math.random() * 100);
    setNum1(random1);
    setNum2(random2);
    setOperationResult(null);
    setOperationSteps([]);
  };

  const clearResults = () => {
    setOperationResult(null);
    setOperationSteps([]);
    setHighlightedBits([]);
    setCurrentStep('');
  };

  return (
    <PageContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Bit Manipulation Visualizer</Title>
        <Subtitle>Visualize bitwise operations and number representations</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <VisualizationContainer>
            <BitDisplay>
              {renderBitRow('Number 1', num1)}
              {renderBitRow('Number 2', num2)}
              {operationResult !== null && (
                renderBitRow('Result', operationResult, true)
              )}
              
              {operationSteps.length > 0 && (
                <OperationDisplay>
                  <OperationTitle>{operations[selectedOperation].name}</OperationTitle>
                  <OperationSteps>
                    {operationSteps.map((step, index) => (
                      <StepItem key={index}>{step}</StepItem>
                    ))}
                  </OperationSteps>
                </OperationDisplay>
              )}
            </BitDisplay>
          </VisualizationContainer>
        </LeftPanel>
        
        <RightPanel>
          <ControlGroup>
            <Label>Select Operation</Label>
            <AlgorithmSelector
              value={selectedOperation}
              onChange={(e) => setSelectedOperation(e.target.value)}
            >
              {Object.entries(operations).map(([key, op]) => (
                <option key={key} value={key}>{op.name}</option>
              ))}
            </AlgorithmSelector>
            
            <Label>Number 1</Label>
            <Input
              type="number"
              value={num1}
              onChange={(e) => setNum1(parseInt(e.target.value) || 0)}
              disabled={isVisualizing}
            />
            
            <Label>Number 2</Label>
            <Input
              type="number"
              value={num2}
              onChange={(e) => setNum2(parseInt(e.target.value) || 0)}
              disabled={isVisualizing}
            />
            
            <Button
              onClick={performOperation}
              disabled={isVisualizing}
            >
              {isVisualizing ? 'Processing...' : `Perform ${operations[selectedOperation].symbol}`}
            </Button>
          </ControlGroup>
          
          <ControlGroup>
            <Button
              onClick={generateRandomNumbers}
              disabled={isVisualizing}
              $variant="warning"
            >
              Generate Random Numbers
            </Button>
            
            <Button
              onClick={clearResults}
              disabled={isVisualizing}
              $variant="danger"
            >
              Clear Results
            </Button>
          </ControlGroup>
          
          <InfoPanel>
            <InfoTitle>{operations[selectedOperation].name}</InfoTitle>
            <InfoText>{operations[selectedOperation].description}</InfoText>
            <InfoText><strong>Complexity:</strong> {operations[selectedOperation].complexity}</InfoText>
          </InfoPanel>
          
          <StatusBar>
            <StatusText><strong>Status:</strong> {isVisualizing ? 'Processing...' : 'Ready'}</StatusText>
            {currentStep && (
              <StatusText><strong>Current:</strong> {currentStep}</StatusText>
            )}
          </StatusBar>
          
          <InfoPanel>
            <InfoTitle>Bit Manipulation Tips</InfoTitle>
            <InfoText>• AND (&): Useful for masking and checking if bits are set</InfoText>
            <InfoText>• OR (|): Useful for setting bits</InfoText>
            <InfoText>• XOR (^): Useful for toggling bits and finding differences</InfoText>
            <InfoText>• NOT (~): Inverts all bits</InfoText>
            <InfoText>• Left Shift (&lt;&lt;): Multiplies by 2^n</InfoText>
            <InfoText>• Right Shift (&gt;&gt;): Divides by 2^n</InfoText>
          </InfoPanel>
        </RightPanel>
      </MainContent>
    </PageContainer>
  );
}

export default BitManipulationPage;
